<template>
  <PageTemplate
    :page-name="'change-password'"
    :is-loading="false"
  >
    <FormContainer class="change-password__form-container">
      <div class="change-password__text-block">
        <h2 class="change-password__title">Відновлення паролю</h2>
        <p class="change-password__description">Щоб завершити відновлення, придумайте новий пароль. Використовуйте його
          для входу у UConnect надалі.</p>
      </div>
      <form class="change-password__form" @submit.prevent="handleSubmit">
        <LabeledTextInput
          class="change-password-form__input--password"
          label="Новий пароль"
          placeholder="*********"
          type="password"
          v-model="form.password"
          :error-messages="errors.password ? [errors.password] : []"
          @input="clearError('password')"
        />
        <p class="change-password__input--description">Пароль має містити не менше 8 символів.</p>
        <LabeledTextInput
          class="change-password-form__input--retype-password"
          label="Повторіть пароль"
          placeholder="*********"
          type="password"
          v-model="form.confirmPassword"
          :error-messages="errors.password ? [errors.password] : []"
          @input="clearError('password')"
        />
        <TurnstileWidget
          ref="turnstile"
          @token-received="cfToken = $event"
          @token-expired="cfToken = ''"
        />
        <div class="change-password__controls">
          <OrangeFancyButton
            class="change-password__controls--change-password"
            type="submit"
            :disabled="(!form.password && !form.confirmPassword) || isLoadingChangePassword || !cfToken"
          >
            <SpinnerLoader
              v-show="isLoadingChangePassword"
              class="change-password__controls--loader"
              color="#FFFFFF"
            />
            Оновити
          </OrangeFancyButton>
          <div class="change-password__login">
          <p class="change-password__login-text">
            У вас вже є аккаунт?
            <SmartLink
              class="change-password__link--login"
              :route="{ name: 'login' }"
            >
              Увійти
            </SmartLink>
          </p>
          </div>
        </div>
      </form>
    </FormContainer>
  </PageTemplate>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { FormContainer, PageTemplate } from '@/shared/ui/account';
import { LabeledTextInput, OrangeFancyButton } from '@/shared/ui/form';
import { SmartLink } from '@/shared/ui/link';
import { SpinnerLoader } from '@/shared/ui/loader';
import TurnstileWidget from '@/shared/ui/turnstile/TurnstileWidget';

export default {
  props: {
    email: {
      type: String,
      required: true,
    },
    token: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      cfToken: '',
      form: {
        password: '',
        confirmPassword: '',
      },
      errors: {
        password: '',
      },
    };
  },
  components: { LabeledTextInput, PageTemplate, FormContainer, SmartLink, SpinnerLoader, OrangeFancyButton, TurnstileWidget },
  computed: {
    ...mapGetters('resetLinkEmailModule', ['isLoadingChangePassword', 'getChangePasswordErrorMessage']),
  },
  methods: {
    ...mapActions('resetLinkEmailModule', ['changePassword']),
    validateForm() {
      return this.validatePassword() && this.validatePasswordLength();
    },
    validatePassword() {
      if (this.form.password.trim() !== this.form.confirmPassword.trim()) {
        this.errors.password = 'Паролі не співпадають';

        return false;
      }

      return true;
    },
    validatePasswordLength() {
      if (this.form.password.length < 8) {
        this.errors.password = 'Пароль має бути не менше 8 символів';

        return false;
      }

      return true;
    },
    clearError(field) {
      this.errors[field] = '';
    },
    async updatePassword() {
      try {
        await this.changePassword({
          token: this.token,
          email: this.email,
          password: this.form.password,
          passwordConfirmation: this.form.confirmPassword,
        });
        this.clearError('password');
      } catch (_) {
        this.errors.password = this.getChangePasswordErrorMessage;

        return false;
      }

      return true;
    },
    async login() {
      //await this.login({ email: this.form.email, password: this.form.password });

      // Автовхід після зміни пароля — теж веб-вхід, тому з капчею
      await this.$store.dispatch('LOGIN', {
        email: this.email,
        password: this.form.password,
        cf_token: this.cfToken,
      }).then(() => {
        this.$router.push({ name: 'prime' });

        return true;
      });

      return false;
    },
    async handleSubmit() {
      if (this.validateForm()) {
        // Turnstile-токен одноразовий — не витрачаємо його на вхід, якщо пароль не змінено
        if (await this.updatePassword()) {
          await this.login();
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.change-password {
  &__form-container {
    margin: 170px auto 0;
    width: 600px;

    @media (max-width: 630px) {
      margin: 50px 16px 0;
      width: auto;
    }
  }

  &__title {
    font-family: "IBM Plex Sans", sans-serif;
    font-size: 36px;
    font-weight: 400;
    color: #0C1421;
    text-align: center;
    margin-bottom: 16px;

    @media (max-width: 630px) {
      font-size: 24px;
    }
  }

  &__description {
    font-size: 18px;
    font-weight: 400;
    color: #313957;
    text-align: center;

    @media (max-width: 630px) {
      font-size: 14px;
    }
  }

  &__input--description {
    font-size: 14px;
  }


  &__controls {
    margin-top: 16px;

    &--change-password {
      margin-bottom: 32px;
    }

    &--loader {
      margin-right: 12px;
    }
  }

  &__login {
    display: flex;
    justify-content: center;
  }

  &__login-text {
    margin-right: 8px;
  }

  &__link--login {
    font-family: 'Inter', sans-serif;
    font-size: 16px;
    font-weight: 400;
    color: #6171E4;

    &:hover {
      text-decoration: underline;
    }
  }
}

::v-deep .input-label,
.input-label {
  position: relative;
  font-family: 'Inter', sans-serif;
  font-size: 18px;
  font-weight: 400;
  color: #494E53;
  margin-bottom: 8px;
}

::v-deep .input-text,
.input-text {
  position: relative !important;
  font-family: 'Inter', sans-serif !important;
  font-size: 22px !important;
  font-weight: 400 !important;
  background-color: #EBF2FF !important;
  width: 515px !important;
  border-radius: 16px !important;
  border: none !important;
  color: #494E53 !important;
  padding: 16px 24px !important;

  @media (max-width: 1300px) {
    width: 100% !important;
  }

  &::placeholder {
    color: #8E9DB7 !important;
  }

  &--has-error {
    border: 2px solid #FF3B30 !important;
  }
}

::v-deep .orange-fancy-button__content {
  display: flex;
  justify-content: center;
}

::v-deep .change-password-form__input--retype-password .input-text--error {
  display: none !important;
}
</style>
